import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { formatPrice } from "@/utils/currency";

export function OrdersReport({ reportData }) {
  const { t } = useTranslation();
  const totals = reportData?.totals || { orders: 0, revenue: 0, cancelled: 0, avg_ticket: 0 };
  const dailyData = reportData?.daily?.map(d => ({
    date: d._id?.slice(5) || "",
    pedidos: d.orders,
    receita: Math.round(d.revenue),
  })) || [];

  return (
    <div className="space-y-4" data-testid="orders-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="report-totals">
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.orders.title")}</p>
            <p className="text-3xl font-bold" data-testid="total-orders">{totals.orders}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.orders.revenue")}</p>
            <p className="text-3xl font-bold" data-testid="total-revenue">{formatPrice(totals.revenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.orders.avgTicket")}</p>
            <p className="text-3xl font-bold" data-testid="avg-ticket">{formatPrice(totals.avg_ticket)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.orders.cancelled")}</p>
            <p className="text-3xl font-bold" data-testid="total-cancelled">{totals.cancelled}</p>
          </CardContent>
        </Card>
      </div>

      {dailyData.length > 0 && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase text-muted-foreground">{t("reports.orders.ordersPerDay")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                    <Tooltip />
                    <Bar dataKey="pedidos" fill="#0d9488" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card border-border/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm uppercase text-muted-foreground">{t("reports.orders.revenuePerDay")}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={dailyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                    <Tooltip />
                    <Bar dataKey="receita" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-base uppercase tracking-tight">{t("reports.orders.topProducts")}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reportData?.top_products?.map((p, idx) => (
                <div key={p._id} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">{idx + 1}</span>
                    <span className="font-medium">{p._id}</span>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{formatPrice(p.revenue)}</p>
                    <p className="text-xs text-muted-foreground">{p.quantity} {t("reports.orders.sold")}</p>
                  </div>
                </div>
              ))}
              {(!reportData?.top_products || reportData.top_products.length === 0) && (
                <p className="text-center text-muted-foreground py-4">{t("reports.orders.noData")}</p>
              )}
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-base uppercase tracking-tight">{t("reports.orders.bySource")}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {reportData?.by_source?.map(s => (
                <div key={s._id} className="flex items-center justify-between">
                  <Badge variant="outline">{s._id || t("reports.orders.unknown")}</Badge>
                  <div className="text-right">
                    <p className="font-bold">{formatPrice(s.revenue)}</p>
                    <p className="text-xs text-muted-foreground">{s.count} {t("reports.orders.title").toLowerCase()}</p>
                  </div>
                </div>
              ))}
              {(!reportData?.by_source || reportData.by_source.length === 0) && (
                <p className="text-center text-muted-foreground py-4">{t("reports.orders.noData")}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
